import axios from 'axios';

import { healioSpaceAPIURL } from '../api/baseHealioSpaceAPI';
import { installSessionInterceptors } from '../api/sessionInterceptors';

import { markViewAsEnded, ViewAsSessionInfo } from './viewAsSession';

// HA-447: starting a support "view as" session from the admin console, and the calls the
// view-as tab makes with the session's own token.

export const VIEW_AS_REASONS = [
  { code: 'TROUBLESHOOT_DISPLAY', label: 'Troubleshoot what they see' },
  { code: 'BILLING_QUESTION', label: 'Billing question' },
  { code: 'DATA_DISCREPANCY', label: 'Data discrepancy' },
  { code: 'ONBOARDING_HELP', label: 'Onboarding help' },
] as const;

export type ViewAsReasonCode = (typeof VIEW_AS_REASONS)[number]['code'];

/** The API's ticket reference format (TicketReference.Pattern): this repo's ticket ids. */
export const TICKET_REF_PATTERN = /^HA-[1-9][0-9]{0,4}$/;

export interface ViewAsStart {
  sessionId: string;
  token: string;
  expiresAt: string;
}

/** Why the API refused a start, in words for the console. Unknown codes get a generic line. */
export const VIEW_AS_REFUSALS: Record<string, string> = {
  NO_GRANT: "The practice hasn't granted support access, or its grant has ended.",
  TARGET_NOT_STAFF: 'Only practice admins and practitioners can be viewed.',
  TARGET_DISABLED: 'This account is disabled.',
  TARGET_NOT_FOUND: 'This account no longer exists.',
  INVALID_TICKET_REF: 'Enter the ticket as HA- followed by its number, for example HA-447.',
  INVALID_REASON_CODE: 'Choose a reason.',
};

const adminViewAsApi = axios.create({
  baseURL: `${healioSpaceAPIURL}/api/v1/admin/view-as`,
  withCredentials: true,
});

installSessionInterceptors(adminViewAsApi);

/** The tab's own calls; the session interceptors send the view-as token. */
const viewAsTabApi = axios.create({
  baseURL: `${healioSpaceAPIURL}/api/v1/view-as`,
  withCredentials: true,
});

installSessionInterceptors(viewAsTabApi);

export const startViewAs = async (
  targetUserSub: string,
  reasonCode: ViewAsReasonCode,
  ticketRef: string
): Promise<ViewAsStart> =>
  (await adminViewAsApi.post<ViewAsStart>('', { targetUserSub, reasonCode, ticketRef })).data;

/** The refusal's code from a failed start, if the API gave one. */
export const viewAsRefusalCode = (error: unknown): string | undefined =>
  axios.isAxiosError(error)
    ? (error.response?.data as { code?: string } | undefined)?.code
    : undefined;

export const getViewAsTabSession = async (): Promise<ViewAsSessionInfo> =>
  (await viewAsTabApi.get<ViewAsSessionInfo>('/session')).data;

/** The banner's End: revokes the session on the server, then ends the tab. */
export const endViewAsFromTab = async (): Promise<void> => {
  try {
    await viewAsTabApi.post('/end');
  } finally {
    markViewAsEnded();
  }
};
